import { useCallback } from "react";

import { css } from "@compiled/react";
import { FormattedMessage, useIntl } from "react-intl";

import Heading from "@atlaskit/heading";
import { token } from "@atlaskit/tokens";

import { UbpStep } from "@/components/usage-based-pricing-calculator/src/context";
import {
    useInputValues,
    useInputSetters,
    useStep,
    useSetStep
} from "@/components/usage-based-pricing-calculator/src/hooks";
import { Button } from "@/libraries/design/components";
import { wacToken } from "@/libraries/design/foundations";

import { useGrowthSliderTracking } from "../hooks/tracking";

import { messages } from "./messages";

const sectionStyles = css({
    paddingBlock: token("space.400")
});

const titleWrapperStyles = css({
    borderBlockEnd: `1px solid ${wacToken("color.neutral.light300")}`
});

const titleStyles = css({
    fontFamily: wacToken("font.charlie.display"),
    fontWeight: 700,
    fontSize: "20px",
    lineHeight: "26px",
    color: wacToken("color.primary.black"),
    marginBlockEnd: token("space.100")
});

const subtitleStyles = css({
    fontFamily: wacToken("font.charlie.text"),
    fontSize: "16px",
    lineHeight: "24px",
    color: wacToken("color.neutral.light700"),
    marginBlockEnd: token("space.300")
});

const sliderContainerStyles = css({
    position: "relative",
    marginBlockEnd: token("space.100")
});

const tooltipStyles = css({
    position: "absolute",
    insetBlockStart: "-32px",
    transform: "translateX(-50%)",
    backgroundColor: wacToken("color.primary.black"),
    color: wacToken("color.neutral.light0"),
    padding: "4px 8px",
    borderRadius: "4px",
    fontFamily: wacToken("font.charlie.text"),
    fontSize: "12px",
    fontWeight: 600,
    whiteSpace: "nowrap",
    pointerEvents: "none"
});

const sliderStyles = css({
    "width": "100%",
    "height": "6px",
    "borderRadius": "3px",
    "appearance": "none",
    "WebkitAppearance": "none",
    "outline": "none",
    "background": wacToken("color.neutral.light300"),

    "&::-webkit-slider-thumb": {
        appearance: "none",
        WebkitAppearance: "none",
        width: "24px",
        height: "24px",
        borderRadius: "50%",
        backgroundColor: wacToken("color.primary.blue"),
        cursor: "pointer",
        border: "none",
        boxShadow: token("elevation.shadow.raised")
    }
});

const labelsStyles = css({
    display: "flex",
    justifyContent: "space-between",
    fontFamily: wacToken("font.charlie.text"),
    fontSize: "14px",
    color: wacToken("color.neutral.light700"),
    marginBlockEnd: token("space.300")
});

const proTipStyles = css({
    fontFamily: wacToken("font.charlie.text"),
    fontSize: "14px",
    fontStyle: "italic",
    color: wacToken("color.neutral.light700"),
    marginBlockEnd: token("space.400")
});

const buttonContainerStyles = css({
    display: "flex",
    justifyContent: "center"
});

export const GrowthSlider = () => {
    const step = useStep();
    const intl = useIntl();
    const setStep = useSetStep();
    const { orgChangePercent } = useInputValues();
    const { setOrgChangePercent } = useInputSetters();
    const { fireSliderClickedEvent } = useGrowthSliderTracking();

    const handleSliderChange = useCallback(
        (e: React.ChangeEvent<HTMLInputElement>) => {
            setOrgChangePercent(parseFloat(e.target.value));
        },
        [setOrgChangePercent]
    );

    const handleSliderClick = useCallback(() => {
        fireSliderClickedEvent(orgChangePercent);
    }, [fireSliderClickedEvent, orgChangePercent]);

    const handleNext = useCallback(() => {
        setStep(UbpStep.usageEstimate);
    }, [setStep]);

    if (step < UbpStep.orgChangePercent) {
        return null;
    }

    // Convert orgChangePercent (multiplier like 1.0) to display percentage
    // 0.0 = -100%, 1.0 = 0%, 1.5 = 50%, 3.0 = 200%
    const displayPercent = Math.round((orgChangePercent - 1) * 100);
    const sliderValue = orgChangePercent;
    const thumbPosition = (sliderValue / 3.0) * 100;

    return (
        <div css={sectionStyles}>
            <div css={titleWrapperStyles}>
                <Heading size="medium" as="h2">
                    <span css={titleStyles}>
                        <FormattedMessage {...messages.growthSliderTitle} />
                    </span>
                </Heading>
            </div>
            <p css={subtitleStyles}>
                <FormattedMessage {...messages.growthSliderSubtitle} />
            </p>

            <div css={sliderContainerStyles}>
                <div css={tooltipStyles} style={{ left: `${thumbPosition}%` }}>
                    {displayPercent > 0 ? `+${displayPercent}` : displayPercent}
                    %
                </div>
                <input
                    css={sliderStyles}
                    type="range"
                    min="0"
                    max="3"
                    step="0.01"
                    value={sliderValue}
                    onChange={handleSliderChange}
                    onClick={handleSliderClick}
                    aria-label={intl.formatMessage(
                        messages.growthSliderAriaLabel
                    )}
                />
            </div>

            <div css={labelsStyles}>
                <span>-100%</span>
                <span>50%</span>
                <span>200%</span>
            </div>

            <p css={proTipStyles}>
                <FormattedMessage {...messages.growthSliderProTip} />
            </p>

            <div css={buttonContainerStyles}>
                <Button variant="primary" size="large" onClick={handleNext}>
                    <FormattedMessage {...messages.nextButton} />
                </Button>
            </div>
        </div>
    );
};
